import { Box } from "@mui/material";
import FilterMenu from "../../../../components/filters/FilterMenu";
import SearchFilter from "../../../../components/filters/SearchFilter";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { AWSServiceType, Platforms } from "../../../../utils/constants";
import { IntegrationsMapping } from "../../integrations/IntegrationsMapping";
import {
  getMacOSName,
  getWindowsOSName,
} from "../components/entitiesCharts/hostChart/osVersionDistribution/osVersionHelpers";
import {
  getServerOSVersion,
  platformIcons,
  ServerTypeIconsAndLabels,
} from "../utils";
import { cveOptions } from "./sharedFilters";
import { ALL_OPTION_NAME, filterName } from "./utils";

const getServerTypeOptions = (fetchedEntities) => {
  const types = new Set(
    fetchedEntities
      .map((entity) => {
        if (entity?.aws_service) return entity.aws_service;
        if (entity?.cloudflare_data?.server_type)
          return IntegrationsMapping.cloudflare.displayName;
        if (entity?.azure_vm_data) return entity.azure_vm_data.resource_type;
        return null;
      })
      .filter(Boolean)
  );
  return [
    {
      name: ALL_OPTION_NAME,
      label: "All server types",
      displayName: "All",
    },
    ...Array.from(types).map((type) => {
      if (!ServerTypeIconsAndLabels[type]) {
        // Provide a fallback for types that don't have an icon defined
        return {
          name: type,
          label: type,
          displayName: type,
        };
      }

      const [icon, label] = ServerTypeIconsAndLabels[type];
      return {
        name: type,
        label: (
          <Box display="flex" gap={1} alignItems="center">
            <SvgToIcon IconLogo={icon} width={"18px"} />
            <>{label}</>
          </Box>
        ),
        displayName: label,
      };
    }),
  ];
};

const getServerOSOptions = (fetchedEntities) => {
  const serverSystems = new Set(
    fetchedEntities
      .map((entity) =>
        entity?.aws_service !== AWSServiceType.LAMBDA && entity.platform
          ? entity.platform
          : null
      )
      .filter(Boolean)
  );
  const getServerOSLabel = (serverOS) => {
    const labelComponents = [serverOS];
    for (const [platform, icon] of Object.entries(platformIcons)) {
      if (serverOS.toLowerCase().includes(platform.toLowerCase())) {
        labelComponents.unshift(
          <SvgToIcon key={platform} IconLogo={icon} width={"16px"} />
        );
        break;
      }
    }

    return labelComponents;
  };
  return [
    {
      name: ALL_OPTION_NAME,
      label: "All systems",
      displayName: "All",
    },
    ...Array.from(serverSystems).map((serverOS) => ({
      name: serverOS,
      label: (
        <Box display="flex" gap={1} alignItems="center">
          {getServerOSLabel(serverOS)}
        </Box>
      ),
      displayName: serverOS,
    })),
  ];
};

const getServerOSVersionOptions = (fetchedEntities, appliedFilters) => {
  const checkedOS = Object.entries(appliedFilters?.[filterName.SERVER_OS] || {})
    .filter(([_, checked]) => checked)
    .map(([osName, _]) => osName);

  const osVersionList = new Set(
    fetchedEntities
      .filter(
        (entity) =>
          entity?.aws_service !== AWSServiceType.LAMBDA &&
          getServerOSVersion(entity) &&
          getServerOSVersion(entity) !== "None" &&
          (checkedOS.length === 0 ||
            checkedOS.includes(ALL_OPTION_NAME) ||
            checkedOS.includes(entity.platform))
      )
      .map((entity) => {
        const osVersion = getServerOSVersion(entity);
        const platform = entity.platform;
        let displayVersion;
        if (platform === Platforms.WINDOWS) {
          const formatted = getWindowsOSName(osVersion);
          displayVersion = formatted === "Windows" ? osVersion : formatted;
        } else if (platform === Platforms.MACOS) {
          const formatted = getMacOSName(osVersion);
          displayVersion =
            formatted === "" || formatted === "MacOS" ? osVersion : formatted;
        } else {
          displayVersion = osVersion;
        }
        return displayVersion;
      })
      .filter(Boolean)
  );

  return [
    {
      name: ALL_OPTION_NAME,
      label: "All versions",
      displayName: "All",
    },
    ...Array.from(osVersionList)
      .sort()
      .reverse()
      .map((osVersion) => ({
        name: osVersion,
        label: (
          <Box display="flex" gap={1} alignItems="center">
            {osVersion}
          </Box>
        ),
        displayName: osVersion,
      })),
  ];
};

const getServerImage = (fetchedEntities) => {
  const serverImage = new Set(
    fetchedEntities
      .map((entity) =>
        entity?.aws_service !== AWSServiceType.LAMBDA &&
        entity?.aws_data?.image_description
          ? entity.aws_data.image_description
          : null
      )
      .filter(Boolean)
  );
  return [
    {
      name: ALL_OPTION_NAME,
      label: "All images",
      displayName: "All",
    },
    ...Array.from(serverImage).map((serverImage) => ({
      name: serverImage,
      label: (
        <Box display="flex" gap={1} alignItems="center">
          <>{serverImage}</>
        </Box>
      ),
      displayName: serverImage,
    })),
  ];
};

const getServerSecurityGroupsOptions = (fetchedEntities) => {
  const securityGroupNames = new Set(
    fetchedEntities
      .flatMap((entity) =>
        entity?.aws_data?.security_groups?.length > 0
          ? entity.aws_data.security_groups.map(
              (securityGroup) => securityGroup.name
            )
          : null
      )
      .filter(Boolean)
  );

  return [
    {
      name: ALL_OPTION_NAME,
      label: "All network security groups",
      displayName: "All",
    },
    ...Array.from(securityGroupNames)
      .sort()
      .map((securityGroup) => ({
        name: securityGroup,
        label: (
          <Box display="flex" gap={1} alignItems="center">
            <>{securityGroup}</>
          </Box>
        ),
        displayName: securityGroup,
      })),
  ];
};

export const internetExposureOption = {
  EXPOSED: "Exposed",
  NOT_EXPOSED: "Not Exposed",
};

const internetExposureOptions = [
  {
    name: ALL_OPTION_NAME,
    label: "All statuses",
    displayName: "All",
  },
  {
    name: internetExposureOption.EXPOSED,
    label: internetExposureOption.EXPOSED,
    displayName: internetExposureOption.EXPOSED,
  },
  {
    name: internetExposureOption.NOT_EXPOSED,
    label: internetExposureOption.NOT_EXPOSED,
    displayName: internetExposureOption.NOT_EXPOSED,
  },
];

export const getServerFilters = (params) => [
  <FilterMenu
    filterName={"Type"}
    fieldName={filterName.SERVER_TYPE}
    options={getServerTypeOptions(params.fetchedEntities)}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
  />,
  <FilterMenu
    filterName={"OS/Image"}
    fieldName={filterName.SERVER_OS}
    options={getServerOSOptions(params.fetchedEntities)}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
    showSearch={true}
  />,
  <FilterMenu
    filterName={"OS Version"}
    fieldName={filterName.SERVER_OS_VERSION}
    options={getServerOSVersionOptions(
      params.fetchedEntities,
      params.appliedFilters
    )}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
    showSearch={true}
  />,
  <FilterMenu
    filterName={"Image"}
    fieldName={filterName.SERVER_IMAGE}
    options={getServerImage(params.fetchedEntities)}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
    showSearch={true}
  />,
  <FilterMenu
    filterName={"Internet Exposure"}
    fieldName={filterName.INTERNET_EXPOSURE}
    options={internetExposureOptions}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
  />,
  <FilterMenu
    filterName={"CVE"}
    fieldName={filterName.CVE}
    options={cveOptions}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
  />,
  <SearchFilter
    filterName={"Network Security Groups"}
    fieldName={filterName.SECURITY_GROUPS}
    options={getServerSecurityGroupsOptions(params.fetchedEntities)}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
  />,
];
